import { useCallback, useEffect, useState } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { apiClient } from "@/lib/api"
import type { ProgressPayload, OverviewPayload } from "../overview/types"

interface UseProgressDataReturn {
  progress: ProgressPayload | null
  overview: OverviewPayload | null
  loading: boolean
  error: string
  refresh: () => void
}

export function useProgressData(): UseProgressDataReturn {
  const { propertyId } = usePropertyContext()
  const [progress, setProgress] = useState<ProgressPayload | null>(null)
  const [overview, setOverview] = useState<OverviewPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const load = useCallback(async (silent = false) => {
    if (!propertyId) return
    if (!silent) {
      setLoading(true)
      setError("")
    }

    try {
      // Fetch both in parallel
      const [progressRes, overviewRes] = await Promise.allSettled([
        apiClient.getPropertyProgress(propertyId),
        apiClient.getPropertyOverview(propertyId),
      ])

      if (progressRes.status === "fulfilled" && progressRes.value.ok && progressRes.value.progress) {
        setProgress(progressRes.value.progress as ProgressPayload)
      } else if (!silent) {
        setError("Unable to load progress data.")
      }

      if (overviewRes.status === "fulfilled" && overviewRes.value.ok) {
        setOverview(overviewRes.value as OverviewPayload)
      }
      // Overview failure is non-critical — page degrades gracefully

    } catch {
      if (!silent) setError("Unable to load progress data.")
    }

    if (!silent) setLoading(false)
  }, [propertyId])

  useEffect(() => {
    load(false)
  }, [load])

  const refresh = useCallback(() => {
    load(true)
  }, [load])

  return { progress, overview, loading, error, refresh }
}
